/**
 * Custom GA4 events for the partner portal.
 *
 * Same shape as app-frontend's src/constant/google-analytics/GAEvents.ts, so an
 * event reads the same in both codebases: a key in GA_LABEL_ENUMS, its
 * `{ category, action, label }` in one of the per-area files beside this one,
 * and `triggerGAEvent(key, custom_label, custom_action, preAction, custom_object)`
 * at the call site.
 *
 * Where it differs from app-frontend, deliberately:
 * - It calls the `gtag` that public/index.html loads (one GA property per deploy
 *   branch, see amplify.yml) instead of react-ga4, because a second tracker
 *   would double-count every page view. With no tag loaded (local, PAT,
 *   previews) an event is only logged to the console.
 * - Event names are built exactly as app-frontend builds them: `preAction` +
 *   `label` + `-custom_action`, so the tracking sheet's names ("Partner portal
 *   events" tab: `sidebar-dashboard-click`, …) go out verbatim. The name is cut
 *   at 40 characters, GA's limit, so a label must stay within that.
 * - Like app-frontend, every event is ALSO posted to app-backend's
 *   `/app/v1/facebook/pixelEvent` relay (→ GA events SQS queue → Ariadne), with
 *   the same payload shape (`event_name`, `custom`, `context`, `session`,
 *   `user`, `env`, `type: "lambda"`). Once signed in, `user.email` is the
 *   operator's email, as app-frontend sends its traveller's. The email goes
 *   to the relay only, never to GA, which forbids PII.
 * - `custom_object` goes to GA itself as event params too, not only to the
 *   relay. GA forbids PII, so never put an email, name or phone in it.
 * - Every event fired while a partner session exists carries `partner_id` (the
 *   session's partnerCode). It is read at fire time, so a sign-out and a
 *   sign-in as another partner in the same tab can never leave it stale, and an
 *   event before sign-in (or after sign-out) has none.
 */
import { pixelEventRequest, sendPixelEvent } from 'Api/analyticsAPI';
import { environment } from 'constants/environment';
import { ENVIRONMENT_TYPE } from 'constants/common';
import { consoleLog } from 'helpers';
import { loadPortalSession } from 'helpers/portalSession';

import { auth_events } from './auth-events';
import { bookings_events } from './bookings-events';
import { branding_events } from './branding-events';
import { commercials_events } from './commercials-events';
import { onboarding_events } from './onboarding-events';
import { platform_events } from './platform-events';
import { sidebar_events } from './sidebar-events';

// Strict string, like rbacConfig's flags and app-frontend's own: only the exact
// 'true' sends custom events. GA's automatic page views are unaffected.
const GA_CUSTOM_EVENTS_ENABLED = process.env.REACT_APP_ENABLE_GA_CUSTOM_EVENTS === 'true';

const PORTAL = 'partner';
const GA_EVENT_NAME_MAX = 40;

export const GA_EVENTS = {
  ...onboarding_events,
  ...sidebar_events,
  ...auth_events,
  ...bookings_events,
  ...commercials_events,
  ...branding_events,
  ...platform_events,
};

/** Every event key, as `GA_LABEL_ENUMS.sidebar_dashboard_click`. */
export const GA_LABEL_ENUMS = Object.freeze(
  Object.fromEntries(Object.keys(GA_EVENTS).map((key) => [key, key])),
);


const readCookie = (name) =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1] || '';

/** One id per tab, like app-frontend's getSessionId, so the relay can group a visit. */
const portalSessionId = () => {
  const KEY = '_zenvoya_portal_analytics_session';
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};

/**
 * GA's ids, read from its cookies exactly as app-frontend does, so Ariadne can tie
 * a relayed event to the GA user and session: `_ga` is GA1.1.<client>.<timestamp>,
 * `_ga_<property>` is GS1.1.<session start>.<session count>….
 */
const gaIdentity = () => {
  const parts = readCookie('_ga').split('.');
  const sessionParts = (document.cookie.split('; ').find((row) => row.startsWith('_ga_'))?.split('=')[1] || '').split('.');
  return {
    user_pseudo_id: parts.length >= 4 ? `${parts[2]}.${parts[3]}` : '',
    ga_session_id: parseInt(parts[3], 10) || Date.now(),
    ga_session_number: parseInt(sessionParts[3], 10) || 1,
  };
};

const withoutEmptyValues = (obj) =>
  Object.fromEntries(
    Object.entries(obj)
      .map(([key, value]) => [key, value && typeof value === 'object' && !Array.isArray(value) ? withoutEmptyValues(value) : value])
      .filter(([, value]) => value !== '' && value !== null && value !== undefined
        && !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)),
  );

/** app-frontend's Pixel/Lambda payload, minus its ad-click fields (fbp/fbc/gclid), which a portal never has. */
const pixelEventData = (eventName, params, email) => {
  const { event_category, event_label, ...custom } = params;
  const { user_pseudo_id, ga_session_id, ga_session_number } = gaIdentity();
  return withoutEmptyValues({
    event_name: eventName,
    sent_at: Math.floor(Date.now() * 1000),
    event_source_url: window.location.href,
    custom,
    max_touch_points: navigator.maxTouchPoints || 0,
    context: {
      app: window.location.host,
      portal: PORTAL,
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
      page_path: window.location.pathname,
    },
    session: { ga_session_id, ga_session_number, session_id: portalSessionId() },
    user: { user_pseudo_id, email },
    env: environment === ENVIRONMENT_TYPE.PRODUCTION ? 'PROD' : 'DEV',
    type: 'lambda',
  });
};

export const triggerGAEvent = (GAlabel, custom_label, custom_action, preAction, custom_object) => {
  const { category = '', action = '', label = '' } = GA_EVENTS[GAlabel] || {};
  const built = (preAction || '') + label + (custom_action ? `-${String(custom_action).replaceAll(' ', '-')}` : '');
  const eventName = (built.includes('-') ? built.toLowerCase() : built).slice(0, GA_EVENT_NAME_MAX);
  if (!eventName) return;

  const params = {
    event_category: category,
    event_label: custom_label || action,
    ...(custom_object || {}),
  };
  // Read once, at fire time: before sign-in (or after sign-out) there is no
  // session, so neither partner_id nor the email goes out.
  const session = loadPortalSession(PORTAL);
  const partnerId = session?.partner?.partnerCode ? String(session.partner.partnerCode) : '';
  if (partnerId) params.partner_id = partnerId;

  const sendToGa = GA_CUSTOM_EVENTS_ENABLED && typeof window.gtag === 'function';
  const pixelEvent = pixelEventRequest(eventName, pixelEventData(eventName, params, session?.user?.email || ''));

  // The complete payload, both destinations, in one line.
  consoleLog(['GA EVENTS', eventName, { gtag: sendToGa ? params : 'not sent (no GA tag on this build)', pixelEvent }]);

  // GA's own events are gated by the flag and need the tag; the relay is not,
  // the same split as app-frontend's triggerGAEvent.
  if (sendToGa) {
    try {
      window.gtag('event', eventName, params);
    } catch (e) {
      consoleLog(e);
    }
  }
  sendPixelEvent(eventName, pixelEvent);
};
